// @flow
import type { NaurFinding } from '../../services/naurService';

export const INITIALIZATION_FAILURE = {
  BLOCKED: 'BLOCKED',
  RETRYABLE: 'RETRYABLE',
};

const isPositive = (value: mixed): boolean => {
  const asNumber = Number(value);
  return Number.isFinite(asNumber) && asNumber > 0;
};

/**
 * A fraction of 0 is "not configured" on the backend, so the test is strictly greater
 * than zero. Users who want an effective zero enter 0.01.
 */
export const hasConfiguredApplicableFraction = (building: ?Object): boolean =>
  isPositive(building?.applicableFractionUnit) ||
  isPositive(building?.applicableFractionFloorSpace);

/**
 * Product's rule: every building needs at least one fraction, or the action is disabled.
 * This is also what "the property is subject to NAUR" means — there is no separate
 * backend concept for it.
 */
export const allBuildingsHaveApplicableFraction = (
  buildings: ?Array<Object>,
): boolean =>
  Array.isArray(buildings) &&
  buildings.length > 0 &&
  buildings.every(hasConfiguredApplicableFraction);

/**
 * Only BLOCKER blocks. Anything else is context, including a severity added later.
 */
export const partitionFindings = (
  findings: ?Array<NaurFinding>,
): { blockers: NaurFinding[], infos: NaurFinding[] } => {
  const all = Array.isArray(findings) ? findings : [];

  return {
    blockers: all.filter((item) => item?.severity === 'BLOCKER'),
    infos: all.filter((item) => item?.severity !== 'BLOCKER'),
  };
};

/**
 * Keys on the body's shape, not the HTTP status: a non-empty `findings` array is the
 * blocked case, since the 422 body is byte-identical to the preflight payload. Everything
 * else is retryable. `error` may be the parsed error body (an object) or a bare string —
 * `fullError` only rejects with the parsed body when it is a non-null object.
 */
export const classifyInitializationFailure = (
  error: ?Object | ?string,
): Object => {
  const body = typeof error === 'object' && error !== null ? error : null;
  const findings = body?.findings;

  if (Array.isArray(findings) && findings.length > 0) {
    return { kind: INITIALIZATION_FAILURE.BLOCKED, preflight: body ?? {} };
  }

  return {
    kind: INITIALIZATION_FAILURE.RETRYABLE,
    message: body?.message ?? body?.networkError ?? null,
  };
};
